<template>
  <div data-testid="groups-trail-row" class="groups-trail-row">
    <!--
      Phase 7.y — Guardrail #4. priorReadFailed banner sits ABOVE the
      diff sections so the dealer reads the constraint before they
      see any segment changes. The raw boolean is NEVER surfaced.
    -->
    <div
      v-if="priorReadFailed"
      class="text-caption text-medium-emphasis mb-2"
      data-testid="groups-trail-prior-read-failed-banner"
    >
      {{ priorReadFailedCopy }}
    </div>

    <!-- Added segments — always rendered when non-empty (even under
         priorReadFailed; additions remain trustworthy). -->
    <section v-if="added.length > 0" data-testid="groups-trail-added" class="mt-1">
      <div class="text-caption font-weight-bold">Added</div>
      <ul class="ma-0 pa-0 segment-list">
        <li v-for="seg in addedDisplayed" :key="`add:${seg.id}`">
          {{ seg.name || seg.id }}
        </li>
      </ul>
      <button
        v-if="addedHasOverflow"
        type="button"
        class="collapse-toggle"
        data-testid="groups-trail-collapse-toggle"
        @click="addedExpanded = !addedExpanded"
      >
        {{ addedExpanded ? 'Show less' : addedCollapseLabel }}
      </button>
    </section>

    <!--
      Removed segments — Guardrail #4: this whole section is OMITTED
      under priorReadFailed. We never compute removals against
      unknown prior state and never render an "unknown" placeholder.
    -->
    <section
      v-if="!priorReadFailed && removed.length > 0"
      data-testid="groups-trail-removed"
      class="mt-2"
    >
      <div class="text-caption font-weight-bold">Removed</div>
      <ul class="ma-0 pa-0 segment-list">
        <li v-for="seg in removedDisplayed" :key="`rem:${seg.id}`">
          {{ seg.name || seg.id }}
        </li>
      </ul>
      <button
        v-if="removedHasOverflow"
        type="button"
        class="collapse-toggle"
        data-testid="groups-trail-collapse-toggle"
        @click="removedExpanded = !removedExpanded"
      >
        {{ removedExpanded ? 'Show less' : removedCollapseLabel }}
      </button>
    </section>

    <!--
      Reasons — Guardrail #5. Render each entry's `explanation`
      verbatim. Section is OMITTED entirely when there are no entries
      OR every entry's explanation is falsy. Missing `reasons` (undef
      / null) is handled by the same `??` defensive coalesce; the
      component must not crash on it.
    -->
    <section v-if="reasons.length > 0" data-testid="groups-trail-reasons" class="mt-2">
      <div class="text-caption font-weight-bold">Reasons</div>
      <ul class="ma-0 pa-0 reason-list">
        <li v-for="(reason, idx) in reasons" :key="`reason:${idx}`">
          {{ reason.explanation }}
        </li>
      </ul>
    </section>
  </div>
</template>

<script lang="ts" setup>
/**
 * Phase 7.y — GroupsDecisionTrailRow
 *
 * Renders a single groups-stage decision record inside the VIN-trail
 * dialog. Stays presentational: no store reads, no fetch logic, no
 * navigation. The parent (`DetailedPublishImpactDialog.vue`) decides
 * which records flow into this row.
 *
 * Guardrail #3 — Added / Removed are computed by **segment id only**.
 * Names are display-only and read from the entry that survived the
 * diff. A segment whose `id` is unchanged but whose `name` differs
 * does NOT appear in either list.
 *
 * Guardrail #4 — When `record.provenance?.priorReadFailed === true`:
 * - render the friendly banner (via the labels helper),
 * - render Added,
 * - OMIT Removed entirely (no inferred removals).
 *
 * Guardrail #5 — `reasons` rendered verbatim; section omitted when
 * empty / missing / all-empty-explanations. Component must not crash
 * when `reasons` is undefined.
 *
 * Guardrail #8 — Both Added and Removed lists collapse identically
 * at `SEGMENT_LIST_COLLAPSE_THRESHOLD`. The constant is imported
 * (no inline `5`).
 */
import { computed, ref } from 'vue'
import {
  publishFlowGroupsPriorReadFailedCopy,
  publishFlowGroupsSegmentsCollapseCopy,
  SEGMENT_LIST_COLLAPSE_THRESHOLD,
} from '@/utils/publishFlowStageLabels'
import type { PublishDecisionRecord } from '@/services/publishPreviewArtifactsService'

interface SegmentRef {
  id: string
  name?: string
}

interface ReasonView {
  explanation: string
}

const props = defineProps<{
  record: PublishDecisionRecord
}>()

const priorReadFailed = computed(
  () => props.record.provenance?.priorReadFailed === true,
)

const priorReadFailedCopy = computed(() => publishFlowGroupsPriorReadFailedCopy())

/**
 * Defensive segment extraction. The decision-record contract has
 * `before?: unknown` / `after?: unknown` because Preview and Publish
 * Flow share one shape; here we narrow into the writer's documented
 * shape `{ segments: { id, name? }[] }` and silently coerce anything
 * unexpected into `[]` so a malformed record never crashes the row.
 */
function extractSegments(value: unknown): SegmentRef[] {
  if (!value || typeof value !== 'object') return []
  const maybe = (value as { segments?: unknown }).segments
  if (!Array.isArray(maybe)) return []
  const out: SegmentRef[] = []
  for (const entry of maybe) {
    if (!entry || typeof entry !== 'object') continue
    const id = (entry as { id?: unknown }).id
    if (typeof id !== 'string' || id.length === 0) continue
    const name = (entry as { name?: unknown }).name
    out.push(typeof name === 'string' && name.length > 0 ? { id, name } : { id })
  }
  return out
}

const beforeSegments = computed<SegmentRef[]>(() => extractSegments(props.record.before))
const afterSegments = computed<SegmentRef[]>(() => extractSegments(props.record.after))

/**
 * Guardrail #3 — diff by id only. Sets are constructed once per
 * computed re-evaluation; small N (typically << 100), so the cost
 * is negligible and Map/Set keep the intent explicit.
 */
const added = computed<SegmentRef[]>(() => {
  if (afterSegments.value.length === 0) return []
  const beforeIds = new Set(beforeSegments.value.map((s) => s.id))
  return afterSegments.value.filter((s) => !beforeIds.has(s.id))
})

const removed = computed<SegmentRef[]>(() => {
  // Defensive even though the template gates this section on
  // `!priorReadFailed`: callers reading `removed` programmatically
  // (tests, future consumers) get the same guarantee.
  if (priorReadFailed.value) return []
  if (beforeSegments.value.length === 0) return []
  const afterIds = new Set(afterSegments.value.map((s) => s.id))
  return beforeSegments.value.filter((s) => !afterIds.has(s.id))
})

/**
 * Guardrail #5 — `reasons` rendering. Coalesce undefined/null to []
 * (must not crash); then drop entries whose `explanation` is empty
 * or whitespace-only so an empty-but-present array doesn't surface
 * a header with nothing under it.
 */
const reasons = computed<ReasonView[]>(() => {
  const raw = props.record.reasons
  if (!Array.isArray(raw)) return []
  const out: ReasonView[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const explanation = (entry as { explanation?: unknown }).explanation
    if (typeof explanation !== 'string') continue
    const trimmed = explanation.trim()
    if (trimmed.length === 0) continue
    // Render the ORIGINAL string (not the trimmed copy) — the writer
    // is the source of truth for casing/punctuation/whitespace.
    out.push({ explanation })
  }
  return out
})

// =============================================================================
// Guardrail #8 — Collapse behavior (identical for Added and Removed).
// =============================================================================

const addedExpanded = ref(false)
const removedExpanded = ref(false)

const addedHasOverflow = computed(
  () => added.value.length > SEGMENT_LIST_COLLAPSE_THRESHOLD,
)
const removedHasOverflow = computed(
  () => removed.value.length > SEGMENT_LIST_COLLAPSE_THRESHOLD,
)

const addedDisplayed = computed<SegmentRef[]>(() =>
  addedExpanded.value || !addedHasOverflow.value
    ? added.value
    : added.value.slice(0, SEGMENT_LIST_COLLAPSE_THRESHOLD),
)
const removedDisplayed = computed<SegmentRef[]>(() =>
  removedExpanded.value || !removedHasOverflow.value
    ? removed.value
    : removed.value.slice(0, SEGMENT_LIST_COLLAPSE_THRESHOLD),
)

const addedCollapseLabel = computed(() =>
  publishFlowGroupsSegmentsCollapseCopy(
    added.value.length - SEGMENT_LIST_COLLAPSE_THRESHOLD,
  ),
)
const removedCollapseLabel = computed(() =>
  publishFlowGroupsSegmentsCollapseCopy(
    removed.value.length - SEGMENT_LIST_COLLAPSE_THRESHOLD,
  ),
)
</script>

<style scoped>
.groups-trail-row {
  /* The parent v-list-item already supplies vertical rhythm. */
}
.segment-list,
.reason-list {
  list-style: disc inside;
  margin: 0;
  padding: 0;
}
.collapse-toggle {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin-top: 4px;
  color: #1d4ed8;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}
.collapse-toggle:hover {
  text-decoration: underline;
}
</style>
